import React, { useRef, useEffect, useState } from 'react';
import { RACButton } from '@rentacenter/racstrap';

interface CustomTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  minHeight?: number;
  maxHeight?: number;
  placeholder?: string;
  disabled?: boolean;

  // Optional button props
  showButton?: boolean;
  buttonLabel?: string;
  onButtonClick?: () => void;
  buttonDisabled?: boolean;
}

const CustomTextArea: React.FC<CustomTextAreaProps> = ({
  value,
  onChange,
  minHeight = 100,
  maxHeight = 300,
  placeholder,
  disabled,

  showButton = false,
  buttonLabel = 'Action',
  onButtonClick,
  buttonDisabled = false,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [textAreaHeight, setTextAreaHeight] = useState(minHeight);

  useEffect(() => {
    const el = textAreaRef.current;
    if (!el) return;

    // Measure actual content height
    const prevHeight = el.style.height;
    el.style.height = 'auto';
    const scrollHeight = el.scrollHeight;
    el.style.height = prevHeight;

    // Cap at maxHeight
    const clamped = Math.min(scrollHeight, maxHeight);
    setTextAreaHeight(clamped);
  }, [value, maxHeight]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
        background: '#fff',
        padding: '4px',
      }}
    >
      <textarea
        ref={textAreaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          height: textAreaHeight,
          minHeight,
          maxHeight, // used only for measuring/clamp logic
          overflowY: textAreaHeight >= maxHeight ? 'auto' : 'hidden',
          resize: 'none',
          width: '100%',
          border: 'none',
          outline: 'none',
          fontSize: '16px',
          padding: '8px',
          boxSizing: 'border-box',
          backgroundColor: disabled ? 'rgb(235, 235, 228)' : 'transparent',
        }}
      />

      {showButton && (
        <div style={{ marginTop: 8 }}>
          {/* Separator line above button row */}
          <div
            style={{
              borderTop: '1px solid #e5e5e5',
              marginBottom: 8,
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <RACButton
              variant="contained"
              color="primary"
              onClick={onButtonClick}
              disabled={buttonDisabled}
              style={{
                padding: '6px 12px',
                fontSize: '14px',
                borderRadius: '4px',
              }}
            >
              {buttonLabel}
            </RACButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTextArea;
